/**
 * vCard 3.0 řetězec pro QR vizitku kontaktu.
 * Port z frontend/index.html — v2GenSimpleContactQR a _buildVcardCompact.
 */

export interface VCardInput {
  name: string;
  role?: string;
  org?: string;
  phone?: string;
  email?: string;
}

function esc(s: string): string {
  return s.replace(/([,;\\])/g, '\\$1').replace(/\r?\n/g, '\\n');
}

export function buildVCard({ name, role, org, phone, email }: VCardInput): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  const lastName = parts.length > 1 ? parts.slice(-1)[0] : trimmed;
  const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';

  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${esc(lastName)};${esc(firstName)}`,
    `FN:${esc(trimmed)}`,
  ];
  if (org) lines.push(`ORG:${esc(org)}`);
  if (role) lines.push(`TITLE:${esc(role)}`);
  if (phone) lines.push(`TEL;TYPE=WORK:${phone.replace(/\s/g, '')}`);
  if (email) lines.push(`EMAIL;TYPE=WORK:${email.trim()}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

export interface CompanyVCardInput {
  nazev?: string;
  obor?: string;
  role?: string;
  ico?: string;
  dic?: string;
  adresa?: string;
  kontakt_jmeno?: string;
  kontakt_tel?: string;
  kontakt_email?: string;
  stavbyvedouci?: string;
  stavbyvedouci_tel?: string;
}

/**
 * Bohatší vCard pro celou firmu (subdodavatele) — zahrnuje název firmy jako ORG,
 * kontaktní osobu, telefon, e-mail, adresu a IČO/DIČ v poznámce. Vhodné ke sdílení
 * přes nativní „sdílet" (WhatsApp, Zprávy…) nebo stažení do adresáře telefonu.
 */
export function buildCompanyVCard(c: CompanyVCardInput): string {
  const nazev = (c.nazev ?? '').trim();
  const kontakt = (c.kontakt_jmeno ?? '').trim();
  // Zobrazované jméno karty: když je kontaktní osoba, použij ji; jinak název firmy.
  const display = kontakt || nazev;
  if (!display) return '';

  // Jméno/příjmení rozděl jen u skutečné osoby; u firmy bez kontaktu nech
  // celý název pohromadě (jinak by vzniklo nesmyslné „N:s.r.o.;BULDIX").
  let nField: string;
  if (kontakt) {
    const parts = display.split(/\s+/);
    const lastName = parts.length > 1 ? parts.slice(-1)[0] : display;
    const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
    nField = `${esc(lastName)};${esc(firstName)};;;`;
  } else {
    nField = `${esc(display)};;;;`;
  }

  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${nField}`,
    `FN:${esc(display)}`,
  ];
  if (nazev) lines.push(`ORG:${esc(nazev)}`);
  const title = (c.obor || c.role || '').trim();
  if (kontakt && title) lines.push(`TITLE:${esc(title)}`);

  const tel = (c.kontakt_tel ?? '').trim();
  if (tel) lines.push(`TEL;TYPE=WORK,VOICE:${tel.replace(/\s/g, '')}`);
  const svTel = (c.stavbyvedouci_tel ?? '').trim();
  if (svTel && svTel.replace(/\s/g, '') !== tel.replace(/\s/g, '')) {
    lines.push(`TEL;TYPE=CELL,VOICE:${svTel.replace(/\s/g, '')}`);
  }

  const email = (c.kontakt_email ?? '').trim();
  if (email) lines.push(`EMAIL;TYPE=WORK:${esc(email)}`);

  const adresa = (c.adresa ?? '').trim();
  if (adresa) lines.push(`ADR;TYPE=WORK:;;${esc(adresa)};;;;`);

  const noteParts: string[] = [];
  if ((c.ico ?? '').trim()) noteParts.push(`IČO: ${c.ico!.trim()}`);
  if ((c.dic ?? '').trim()) noteParts.push(`DIČ: ${c.dic!.trim()}`);
  if (kontakt && (c.stavbyvedouci ?? '').trim()) {
    noteParts.push(`Stavbyvedoucí: ${c.stavbyvedouci!.trim()}`);
  }
  if (noteParts.length) lines.push(`NOTE:${esc(noteParts.join(' · '))}`);

  lines.push('END:VCARD');
  return lines.join('\r\n');
}

export interface ParsedVCard {
  nazev: string;
  kontakt_jmeno: string;
  kontakt_tel: string;
  kontakt_email: string;
  adresa: string;
  obor: string;
  ico: string;
  dic: string;
}

function unescapeValue(s: string): string {
  return s
    .replace(/\\n/gi, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

/** Dekóduje QUOTED-PRINTABLE (starší vCardy z telefonů s diakritikou). */
function decodeQuotedPrintable(s: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '=' && i + 2 < s.length) {
      const hex = s.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        bytes.push(parseInt(hex, 16));
        i += 2;
        continue;
      }
    }
    bytes.push(s.charCodeAt(i) & 0xff);
  }
  try {
    return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
  } catch {
    return s;
  }
}

/**
 * Parsuje vCard (.vcf) řetězec do polí firmy/kontaktu. Zvládá vCard 2.1/3.0/4.0,
 * skládání řádků (folding), escapování i QUOTED-PRINTABLE. Vrací `null`, když to
 * není rozpoznatelná vCard nebo chybí jméno. Z NOTE se pokusí vytáhnout IČO/DIČ.
 */
export function parseVCard(raw: string): ParsedVCard | null {
  if (!raw || !/BEGIN:VCARD/i.test(raw)) return null;

  // Rozbal složené řádky: řádek začínající mezerou/tabem pokračuje předchozí.
  const lines = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const unfolded: string[] = [];
  for (const line of lines) {
    if ((line.startsWith(' ') || line.startsWith('\t')) && unfolded.length) {
      unfolded[unfolded.length - 1] += line.slice(1);
    } else {
      unfolded.push(line);
    }
  }

  const result: ParsedVCard = {
    nazev: '', kontakt_jmeno: '', kontakt_tel: '', kontakt_email: '',
    adresa: '', obor: '', ico: '', dic: '',
  };
  let fn = '';
  let n = '';
  let org = '';

  for (const line of unfolded) {
    const idx = line.indexOf(':');
    if (idx < 0) continue;
    const head = line.slice(0, idx);
    const segs = head.split(';');
    const prop = segs[0].toUpperCase();
    const params = segs.slice(1);
    let value = line.slice(idx + 1);
    if (params.some((p) => /ENCODING=QUOTED-PRINTABLE/i.test(p))) {
      value = decodeQuotedPrintable(value);
    }
    value = unescapeValue(value).trim();
    if (!value) continue;

    switch (prop) {
      case 'FN': fn = value; break;
      case 'N': n = value; break;
      case 'ORG': org = value.split(';')[0].trim(); break;
      case 'TITLE': if (!result.obor) result.obor = value; break;
      case 'TEL': if (!result.kontakt_tel) result.kontakt_tel = value; break;
      case 'EMAIL': if (!result.kontakt_email) result.kontakt_email = value; break;
      case 'ADR':
        if (!result.adresa) {
          result.adresa = value.split(';').map((s) => s.trim()).filter(Boolean).join(', ');
        }
        break;
      case 'NOTE': {
        const ico = value.match(/I[ČC]O[:\s]*([0-9]{6,8})/i);
        if (ico) result.ico = ico[1];
        const dic = value.match(/DI[ČC][:\s]*((?:CZ|SK)?[0-9]{6,12})/i);
        if (dic) result.dic = dic[1];
        break;
      }
    }
  }

  // Jméno osoby: preferuj FN, jinak složit z N (příjmení;jméno).
  let personName = fn;
  if (!personName && n) {
    const p = n.split(';');
    personName = [p[1], p[0]].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  }

  if (org) {
    result.nazev = org;
    if (personName && personName.trim() !== org.trim()) result.kontakt_jmeno = personName;
  } else {
    // Bez ORG → kontakt osoby; jméno dáme i do názvu (povinné pole), ať se uloží.
    result.nazev = personName;
    result.kontakt_jmeno = personName;
  }

  return result.nazev ? result : null;
}

/**
 * Sdílí vCard přes nativní share sheet (mobil → WhatsApp, Zprávy, AirDrop…).
 * Když prohlížeč sdílení souborů nepodporuje (typicky desktop), stáhne `.vcf`.
 * Vrací, co se reálně stalo, ať může UI dát správnou zpětnou vazbu.
 */
export async function shareVCard(
  filename: string,
  vcard: string,
  meta?: { title?: string; text?: string },
): Promise<'shared' | 'downloaded' | 'cancelled'> {
  if (!vcard) return 'cancelled';
  const nav = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean;
    share?: (data?: ShareData) => Promise<void>;
  };

  // Nativní sdílení souboru — preferované na mobilu.
  if (typeof File !== 'undefined' && nav.share && nav.canShare) {
    const file = new File([vcard], filename, { type: 'text/vcard' });
    if (nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: meta?.title, text: meta?.text });
        return 'shared';
      } catch (err) {
        // Uživatel zrušil systémové menu — nestahuj na pozadí.
        if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
        // Jiná chyba → spadni na stažení.
      }
    }
  }

  // Fallback: stažení .vcf souboru.
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return 'downloaded';
}

export interface MojeFirmaVCardInput {
  /** Jméno osoby (uživatel) — když chybí, karta je na firmu. */
  osoba?: string;
  nazev: string;
  ico?: string;
  dic?: string;
  adresa?: string;
  pobocka_nazev?: string;
  pobocka_adresa?: string;
  banka_ucet?: string;
  banka_iban?: string;
  email?: string;
  telefon?: string;
  web?: string;
}

/**
 * ADR pole vCard: „Ulice 12, 190 00 Praha" → ulice / město / PSČ zvlášť,
 * aby je telefon uložil do správných kolonek. Jiný formát → celé jako ulice.
 */
function adrField(adresa: string, extended = ''): string {
  const m = adresa.match(/^(.*?),\s*(\d{3}\s?\d{2})\s+(.+)$/);
  if (!m) return `;${esc(extended)};${esc(adresa)};;;;`;
  const [, ulice, psc, mesto] = m;
  return `;${esc(extended)};${esc(ulice.trim())};${esc(mesto.trim())};;${psc.replace(/\s/g, '')};Česká republika`;
}

/**
 * vCard z vlastních fakturačních údajů („Moje údaje") — k načtení QR kódem
 * do telefonu a uložení jako kontakt. IČO/DIČ/účet jdou do poznámky.
 */
export function buildMojeFirmaVCard(f: MojeFirmaVCardInput): string {
  const nazev = f.nazev.trim();
  const osoba = (f.osoba ?? '').trim();
  const display = osoba || nazev;
  if (!display) return '';

  let nField: string;
  if (osoba) {
    const parts = osoba.split(/\s+/);
    const lastName = parts.length > 1 ? parts.slice(-1)[0] : osoba;
    const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
    nField = `${esc(lastName)};${esc(firstName)};;;`;
  } else {
    nField = `${esc(display)};;;;`;
  }

  const lines = ['BEGIN:VCARD', 'VERSION:3.0', `N:${nField}`, `FN:${esc(display)}`];
  if (nazev) lines.push(`ORG:${esc(nazev)}`);
  const tel = (f.telefon ?? '').replace(/\s/g, '');
  if (tel) lines.push(`TEL;TYPE=WORK,VOICE:${tel}`);
  const email = (f.email ?? '').trim();
  if (email) lines.push(`EMAIL;TYPE=WORK:${esc(email)}`);
  const web = (f.web ?? '').trim();
  if (web) lines.push(`URL:${/^https?:\/\//i.test(web) ? web : `https://${web}`}`);
  const adresa = (f.adresa ?? '').trim();
  if (adresa) lines.push(`ADR;TYPE=WORK:${adrField(adresa)}`);
  const pobocka = (f.pobocka_adresa ?? '').trim();
  if (pobocka) lines.push(`ADR;TYPE=POSTAL:${adrField(pobocka, (f.pobocka_nazev ?? '').trim())}`);

  const note: string[] = [];
  if ((f.ico ?? '').trim()) note.push(`IČO: ${f.ico!.trim()}`);
  if ((f.dic ?? '').trim()) note.push(`DIČ: ${f.dic!.trim()}`);
  if ((f.banka_ucet ?? '').trim()) note.push(`Účet: ${f.banka_ucet!.trim()}`);
  if ((f.banka_iban ?? '').trim()) note.push(`IBAN: ${f.banka_iban!.trim()}`);
  if (note.length) lines.push(`NOTE:${esc(note.join('\n'))}`);

  lines.push('END:VCARD');
  return lines.join('\r\n');
}
